import { ColumnAggregationType } from "utils/enums";
import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { T } from "services/lang/LanguageService";
import { DataTable } from "primereact/datatable";
import { Paginator } from "primereact/paginator";
import { Row } from "primereact/row";
import useSafeId from "hooks/dashboard/useDashboardComponentId";
import React, { CSSProperties, WheelEvent, useCallback, useEffect, useRef, useState } from "react";
import DataCell from "./components/templates/table-components/data-cell/DataCell";
import { AttributeHeader } from "./components/templates/table-components/header/AttributeHeader";
import {
  IColumnsWithValuesProps,
  IProductTemplateAttribute,
  ITableDataEntry,
  ProductTemplateNonTransposedTableProps,
} from "./utils/interfaces";

import {
  checkForHighlightType,
  checkForUnwantedType,
  formatDataTotalsAggregation,
  getHighlightColor,
  getValueColors,
  handlePrecision,
} from "./utils/utils";

const ProductTemplateTableDefault = ({
  templateData,
  configData,
  selectedEntryBody,
  selectedEntryId,
  selectedEntryIndex,
  itemsPerPage,
  editedDataHandler,
  onClickSign,
  handleTableKeyDown,
  EditRowDataCell,
  reportOptions,
  editColumnLocation = "BACK",
  page,
  setPage,
  allowRowEdit,
  foreignKeyWrap,
  showTotals,
  hidePagination
}: ProductTemplateNonTransposedTableProps) => {
  const tableId = useSafeId();
  const divRef = useRef<HTMLDivElement>(null);

  const [selected, setSelected] = useState(null);
  const [rows, setRows] = useState(itemsPerPage);
  const [aggregatedColumns, setAggregatedColumns] = useState<IColumnsWithValuesProps[]>([]);

  const rowsPerPageOptions = itemsPerPage
    ? [itemsPerPage, 10, 20, 30].sort((a, b) => a - b).filter((x, i, a) => a.indexOf(x) === i)
    : [];

  const handleWheel = useCallback((event: WheelEvent<HTMLDivElement>) => {
    if (event.altKey) {
      const div: HTMLDivElement = event.currentTarget as HTMLDivElement;
      div.scrollLeft += event.deltaY * 0.45;
    }
  }, []);

  const dataCell = (
    val: ITableDataEntry,
    index: number,
    configVal: IProductTemplateAttribute,
    configIndex: number
  ) => {
    const dataValue = selectedEntryBody && selectedEntryIndex === index ? selectedEntryBody : val;
    const obj = dataValue.entries[configVal.referenceName];

    let color;
    let bgColor;
    if (obj && obj.type && checkForHighlightType(obj.type)) {
      [color, bgColor] = getValueColors(obj.value as number, {
        highlightRanges: configVal.bgHighlightRanges,
        defaultHighlightColor: configVal.highlightColor,
      });
    } else {
      [color, bgColor] = getHighlightColor(configVal.highlightColor);
    }

    return (
      <div className="text-center" style={styleHandler(color, bgColor, obj && obj.type)}>
        <DataCell
          key={val.id}
          headerConfig={configVal}
          dataValue={dataValue}
          indexHeader={configIndex}
          indexEntry={index}
          changeHandler={editedDataHandler}
          isInEditMode={selectedEntryId === val.id}
          onClickSign={onClickSign}
          foreignKeyWrap={foreignKeyWrap}
          editRequireRoles={configVal.editRequireRoles ?? []}
        />
      </div>
    );
  };

  const styleHandler = (
    color: string | undefined,
    bgColor: string | undefined,
    type: string | undefined
  ): CSSProperties | undefined => {
    if (checkForUnwantedType(type)) {
      if (color && bgColor) {
        return {
          overflowX: "auto",
          color,
          backgroundColor: bgColor,
        };
      }
      if (bgColor) {
        return {
          overflowX: "auto",
          backgroundColor: bgColor,
        };
      }

      if (color) {
        return {
          overflowX: "auto",
          color,
        };
      }
    }

    return {
      overflowX: "auto",
    };
  };

  const editColumn = (frozen = false) => (
    <Column
      key={12}
      className="non-printable"
      frozen={frozen}
      body={(rowData, options) => EditRowDataCell(rowData, options.rowIndex)}
      style={{ width: "0" }}
      hidden={!allowRowEdit}
    />
  );

  const generateFooterColumns = (aggregatedCols: IColumnsWithValuesProps[]) => {
    return aggregatedCols.map(col => {
      let tooltipText = "";
      let footerText: string | number = "";
      if (col.aggregationType === ColumnAggregationType.SUM) {
        tooltipText = T("word.sum");
        footerText = `${handlePrecision(col.total!, col.decimalPlaces!, true)} ${col.unit ?? ""}`;
      } else if (col.aggregationType === ColumnAggregationType.MEAN) {
        tooltipText = T("word.average");
        footerText = `${handlePrecision(col.total!, col.decimalPlaces!, true)} ${col.unit ?? ""}`;
      } else if (col.aggregationType === ColumnAggregationType.COUNT) {
        tooltipText = T("action.count");
        footerText = handlePrecision(col.total!, col.decimalPlaces!, true);
      }
      return (
        <Column
          key={col.referenceName}
          footer={
            <div title={tooltipText} className="text-center">
              {footerText}
            </div>
          }
        />
      );
    });
  };

  let footerGroup = null;
  if (showTotals && aggregatedColumns.length > 0) {
    footerGroup = (
      <ColumnGroup>
        <Row>{generateFooterColumns(aggregatedColumns)}</Row>
      </ColumnGroup>
    );
  }

  useEffect(() => {
    if (reportOptions && document.getElementsByClassName("print-mode-enabled").length > 0) {
      const table = document.querySelector(`#${tableId}`);
      if (table) {
        const allChildren = table.querySelectorAll("*");

        if (reportOptions.fontSize) {
          for (let i = 0; i < allChildren.length; i++) {
            allChildren[i].style.fontSize = reportOptions?.fontSize;
          }
        }
      }
    }
  }, [footerGroup]);

  useEffect(() => {
    setAggregatedColumns(formatDataTotalsAggregation(configData, templateData));
  }, [configData?.data?.attributes, templateData]);

  const dynamicColumns = configData?.data?.attributes.map(
    (col: IProductTemplateAttribute, i: number) => (
      <Column
        key={col.referenceName}
        field={col.referenceName}
        className="change-padding"
        header={<AttributeHeader key={col.referenceName} attribute={col} noTh />}
        body={(rowData: ITableDataEntry, options) => {
          return dataCell(rowData, options.rowIndex, col, i);
        }}
      />
    )
  );

  const allColumns = () => {
    const columns: JSX.Element[] = [...dynamicColumns!];

    if (editColumnLocation === "FRONT") {
      columns.unshift(editColumn(true));
    } else if (editColumnLocation === "BACK") {
      columns.push(editColumn(false));
    } else {
      console.error(T("error.invalid-edit-column-location"));
    }

    return columns;
  };

  return (
    <>
      <div
        className="default-table"
        id={tableId}
        style={{ overflowX: "auto" }}
        onKeyDown={e => handleTableKeyDown(e.key)}
        ref={divRef}
        onWheel={handleWheel}
      >
        <DataTable
          value={rows ? [...templateData.slice(page, page + rows)] : templateData}
          size="small"
          showGridlines
          selectionMode="single"
          selection={selected}
          onSelectionChange={e => setSelected(e.value)}
          footerColumnGroup={footerGroup}
        >
          {allColumns()}
        </DataTable>
      </div>
      {!!itemsPerPage && !hidePagination && (
        <Paginator
          totalRecords={templateData.length}
          rows={rows}
          first={page}
          onPageChange={e => {
            setPage(e.first);
            setRows(e.rows);
          }}
          template="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          currentPageReportTemplate={`${T("word.showing")} {first} ${T("word.to")} {last} ${T("word.of")} {totalRecords}`}
          rowsPerPageOptions={rowsPerPageOptions}
        />
      )}
    </>
  );
};

export default ProductTemplateTableDefault;
